import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AssignmentCustomerResponse, AssignmentPspResponse } from '../../../../api';
import { baseAssignmentsActions, baseAssignmentsSelectors, BaseCoreState } from '../../../_store';
import { BaseButtonSize } from '../../../shared/components/button/base-button.component';
import { BaseModalService } from '../../../shared/services/base-modal.service';
import { BaseFileExtension } from '../../../shared/enums/base-available-file-type.enum';

@Component({
  selector: 'base-employee-id-card-action-buttons',
  templateUrl: './base-employee-id-card-action-buttons.component.html',
})
export class BaseEmployeeIdCardActionButtonsComponent implements OnInit, OnDestroy {
  @ViewChild('idCardViewerTemplate') idCardViewerTemplate: ElementRef;
  @Input() assignment: AssignmentPspResponse | AssignmentCustomerResponse | undefined;

  idCardModalRef: NgbModalRef;
  idCardActiveDocumentSrc: Blob;
  idCardFileName: string;

  buttonSize = BaseButtonSize;
  baseFileExtension = BaseFileExtension;

  private destroy$ = new Subject<boolean>();

  constructor(private store: Store<BaseCoreState>, private modalService: BaseModalService) {}

  ngOnInit() {
    this.subscribeToProfileIDCard();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  downloadIDCard(openInModal?: boolean, downloadOnly?: boolean): void {
    if (!this.assignment) {
      return;
    }

    this.store.dispatch(
      baseAssignmentsActions.downloadAssignmentProfileIDCard({
        payload: { assignment: this.assignment.id, profile: this.assignment.profile.id, openInModal, downloadOnly },
      })
    );
  }

  openRegenerateIDModal(): void {
    this.modalService
      .open(
        {
          headText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalHeadText',
          bodyText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalBodyText',
          okText: 'externalEmployees.masterData.employeeIdCard.regenerate.modalOkText',
        },
        {
          backdrop: 'static',
        }
      )
      .result.then(() => {
        if (!this.assignment) {
          return;
        }

        this.store.dispatch(
          baseAssignmentsActions.regenerateAssignmentProfileIDCard({
            payload: { profile: this.assignment.profile.id, assignment: this.assignment.id },
          })
        );
      }, noop);
  }

  private subscribeToProfileIDCard(): void {
    this.store
      .select(baseAssignmentsSelectors.getAssignmentProfileIDCard)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data)
      )
      .subscribe((idCardView) => {
        this.openIDCardViewer(idCardView.data as Blob, idCardView.fileName as string);
      });
  }

  private openIDCardViewer(document: Blob, fileName: string): void {
    this.idCardActiveDocumentSrc = document;
    this.idCardFileName = fileName;
    this.idCardModalRef = this.modalService.open(
      {
        bodyTemplate: this.idCardViewerTemplate,
        cancelText: 'action.close',
        cancelButtonOnly: true,
        okButtonDisplayed: false,
        hideHeader: true,
      },
      { size: 'md', backdrop: 'static' }
    );
    this.idCardModalRef.result.then(
      () => this.resetIDCardViewer(),
      () => this.resetIDCardViewer()
    );
  }

  private resetIDCardViewer(): void {
    this.store.dispatch(baseAssignmentsActions.clearAssignmentProfileIDCardView());
  }
}
